import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import type { KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useResizableWidth } from '../../../../hooks/useResizableWidth';
import { Button } from '../../../../ui-library/button/button';
import { Text } from '../../../../ui-library/typography/typography';
import { deriveTurnsFromHistory } from '../../../flora/components/flora-message/flora-turn-pairing.helpers';
import type { ChatHistory } from '../../../flora/flora.type';
import { AgentMessageList } from '../../../flora/components/chat/agent-message-list';

interface AgentAutomationRunChatDrawerProps {
  chatHistory: ChatHistory | undefined;
  onClose: () => void;
  onOpenFullChat: () => void;
}

const MIN_DRAWER_WIDTH = 320;
const DEFAULT_DRAWER_WIDTH = 440;

/**
 * Read-only side drawer for the chat created by an automation run. It keeps the
 * runs view in place while reusing the normal message rendering for the saved
 * transcript; no input or follow-up actions are exposed here.
 */
export function AgentAutomationRunChatDrawer({
  chatHistory,
  onClose,
  onOpenFullChat,
}: AgentAutomationRunChatDrawerProps) {
  const drawerRef = useRef<HTMLElement>(null);
  const { width, maxWidth, isResizing, handleMouseDown, setWidth } = useResizableWidth({
    initialWidth: DEFAULT_DRAWER_WIDTH,
    minWidth: MIN_DRAWER_WIDTH,
    maxWidth: () => Math.max(MIN_DRAWER_WIDTH, Math.round(window.innerWidth * 0.5)),
    computeWidth: (event) => {
      const rightEdge = drawerRef.current?.getBoundingClientRect().right ?? window.innerWidth;
      return rightEdge - event.clientX;
    },
  });
  const { questions, answers: messages } = useMemo(
    () => deriveTurnsFromHistory(chatHistory?.messages ?? []),
    [chatHistory],
  );

  const handleResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const step = 24;
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setWidth((currentWidth) => currentWidth + step);
          break;
        case 'ArrowRight':
          event.preventDefault();
          setWidth((currentWidth) => currentWidth - step);
          break;
        case 'Home':
          event.preventDefault();
          setWidth(MIN_DRAWER_WIDTH);
          break;
        case 'End':
          event.preventDefault();
          setWidth(maxWidth);
          break;
        default:
          break;
      }
    },
    [maxWidth, setWidth],
  );

  return (
    <>
      <ResizeHandle
        role="separator"
        tabIndex={0}
        aria-label="Resize run chat"
        aria-orientation="vertical"
        aria-valuemin={MIN_DRAWER_WIDTH}
        aria-valuemax={maxWidth}
        aria-valuenow={width}
        onMouseDown={handleMouseDown}
        onKeyDown={handleResizeKeyDown}
        data-dragging={isResizing}
      >
        <ResizeGrip>
          <IconifyIcon icon="material-symbols:drag-indicator" width={14} height={14} />
        </ResizeGrip>
      </ResizeHandle>
      <Drawer ref={drawerRef} aria-label="Automation run chat" $width={width}>
        <DrawerHeader>
          <DrawerTitle>
            <Text size="medium" weight="bold" color="var(--ink-darkest)">
              Run chat
            </Text>
            <Text size="tiny" color="var(--ink-lighter)">
              {chatHistory?.description || 'Read-only conversation'}
            </Text>
          </DrawerTitle>
          <DrawerActions>
            <Button variant="outline" size="sm" onClick={onOpenFullChat} aria-label="Open chat full page">
              <IconifyIcon icon="material-symbols:open-in-full" width={15} height={15} />
            </Button>
            <Button variant="outline" size="sm" onClick={onClose} aria-label="Close run chat">
              <IconifyIcon icon="material-symbols:close" width={15} height={15} />
            </Button>
          </DrawerActions>
        </DrawerHeader>

        <ReadOnlyNotice>
          <IconifyIcon icon="material-symbols:visibility-outline" width={15} height={15} />
          <Text size="tiny" color="var(--ink-lighter)">
            This automation chat is read-only.
          </Text>
        </ReadOnlyNotice>

        {chatHistory ? (
          <AgentMessageList
            questions={questions}
            messages={messages}
            isWaitingForFirstResponse={false}
            isActiveQuestion={false}
            loadingMessage=""
            showFollowups={false}
          />
        ) : (
          <EmptyState>
            <Text size="small" color="var(--ink-lighter)">
              Loading chat…
            </Text>
          </EmptyState>
        )}
      </Drawer>
    </>
  );
}

const Drawer = styled.aside<{ $width: number }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex-shrink: 0;
  width: ${({ $width }) => `${$width}px`};
  min-width: ${({ $width }) => `${$width}px`};
  background: var(--base-white);
  border-left: 1px solid var(--sky-light);
  box-shadow: -8px 0 20px rgba(19, 47, 76, 0.08);
`;

const ResizeHandle = styled.div`
  width: 5px;
  background: var(--sky-light);
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  transition: background 0.15s;

  &::before {
    content: '';
    position: absolute;
    inset: 0 -3px;
  }

  &:hover,
  &:focus-visible,
  &[data-dragging='true'] {
    background: var(--secondary-light);
  }

  &:hover > span,
  &:focus-visible > span,
  &[data-dragging='true'] > span {
    border-color: var(--secondary-light);
    color: var(--secondary-base);
  }
`;

const ResizeGrip = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-lighter);
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  padding: 2px 0;
  pointer-events: none;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--sky-light);
`;

const DrawerTitle = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DrawerActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;

  button {
    padding: 6px;
  }
`;

const ReadOnlyNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--sky-lightest);
  border-bottom: 1px solid var(--sky-light);
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 24px;
  text-align: center;
`;

export type { AgentAutomationRunChatDrawerProps };
